import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Chip, cn, ToggleButton, ToggleButtonGroup } from '@heroui/react';
import { ACTIVITY_PERIODS, type ActivityPeriod, type ActivityResponse } from '@vlastiq/contracts';
import { fetchActivity } from '../data/api.ts';
import { InlineLoader } from './InlineLoader.tsx';
import { errorText } from '../lib/errors.ts';
import { formatShare, nameActivityShares, PERIOD_LABELS } from '../lib/activity.ts';

/**
 * Who is talking in the group, as shares of the whole.
 *
 * ⚠️ SHARES, NEVER COUNTS, AND THAT IS ENFORCED ON THE WIRE (owner's call,
 * 2026-08-13). `GET /api/activity` carries no per-person total and no
 * denominator, so this component could not render «412 сообщений» if it wanted
 * to. The question is relative — who is around and who is not — and a raw count
 * on a page every member can see invites «у меня 412, а у тебя 38».
 *
 * ⚠️ A BAR LIST, NOT A CHART LIBRARY. recharts is admin-only and lazy-loaded on
 * purpose (DECISION-35); importing it from the home page would undo that trade
 * in one line, for every member, on their phone. The bar is two spans and a
 * width — the same shape `BudgetBar` on Расход ИИ already draws — and it costs
 * the member bundle nothing.
 *
 * ⚠️ AND IT IS A LIST RATHER THAN A PIE OR A STACKED BAR. This is read DOWN a
 * column — «кто вверху, кто внизу» — which is the same argument every table in
 * this app is a table for. A stacked bar needs a colour-to-name legend, which is
 * the exact problem the AI-usage charts hit twice; a pie is unreadable past four
 * people, and this clan has more than four.
 */

/**
 * How many rows before the list folds (owner's call, 2026-08-13).
 *
 * ⚠️ TEN, NOT SIX, BECAUSE THE FOLD WAS FIRING ON THE ORDINARY CASE. Six was
 * chosen to fit a phone card without scrolling, which optimised the wrong
 * thing: a clan of nine put three people behind a button on every single load,
 * so the card's own question — who is around and who is not — could not be
 * answered without pressing something. Ten covers this group whole, and the
 * fold goes back to being what it is for: a list long enough that showing all
 * of it would bury the rest of the page.
 */
const VISIBLE_ROWS = 10;

export function ActivityCard({ ownMemberId }: { ownMemberId: number | null }) {
  const [period, setPeriod] = useState<ActivityPeriod>('month');
  const [data, setData] = useState<ActivityResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  /**
   * A refetch is in flight and the rows on screen are the PREVIOUS period's.
   *
   * ⚠️ THIS EXISTS SO THE LIST DOES NOT EMPTY BETWEEN PERIODS, and that is not
   * a polish detail — it was a measured bug (owner's report, 2026-08-13).
   * Clearing `data` on every switch collapsed the card from 610px to 187px,
   * which took 423px out of the DOCUMENT — and this card is the LAST thing on
   * Главная, so anyone standing at it was scrolled near the bottom. The browser
   * clamped the scroll to the new maximum, and when the rows came back and the
   * page grew again the scroll was NOT restored. Measured: scrollY 1173 → 750 →
   * 750. In Telegram's webview that reads as the page having reloaded and
   * thrown you to the top.
   */
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Which request is the current one.
   *
   * ⚠️ SWITCHING PERIODS LEAVES THE PREVIOUS REQUEST IN FLIGHT, and a slow reply
   * for «Год» landing after a fast one for «Неделя» would put the year's
   * numbers under the week's heading — a confident lie about who has been
   * around, rather than a stale spinner. Same guard, same reason, as the
   * attendance summary's `loadSeq`.
   */
  const loadSeq = useRef(0);

  const load = useCallback(async (which: ActivityPeriod) => {
    const seq = ++loadSeq.current;
    setRefreshing(true);
    try {
      const response = await fetchActivity(which);
      if (seq !== loadSeq.current) return;
      setData(response);
      setLoadError(null);
    } catch (caught) {
      if (seq !== loadSeq.current) return;
      /*
        ⚠️ THE STALE ROWS *ARE* DROPPED ON A FAILURE, unlike on a successful
        switch. Keeping them would leave the previous period's numbers under the
        newly selected label with no way to tell — the confident lie this card
        avoids everywhere else. The card does shrink here, and that is the right
        trade: a failure is rare, it is not a transition, and the reader is given
        a sentence to read in place of the list.
      */
      setData(null);
      setLoadError(errorText(caught, 'Не удалось загрузить активность.'));
    } finally {
      if (seq === loadSeq.current) setRefreshing(false);
    }
  }, []);

  /*
    ⚠️ NEITHER `setData(null)` NOR `setExpanded(false)` HERE, and both were
    wrong for the same reason: they change the card's HEIGHT on a press that
    should only change its numbers.

    Clearing the data emptied the list mid-switch — see `refreshing` above.
    Resetting the fold was the second half of the same complaint: somebody who
    had opened the full list to look at it lost it again on every period they
    tried, so comparing «Месяц» with «Год» meant pressing «Показать ещё» each
    time. The fold is a decision about how much of the list to read, and that
    decision does not stop being true when the window changes.
  */
  useEffect(() => {
    void load(period);
  }, [load, period]);

  const named = nameActivityShares(data?.entries ?? []);
  const shown = expanded ? named : named.slice(0, VISIBLE_ROWS);

  return (
    <Card>
      <Card.Header>
        <Card.Title>Кто пишет в чате</Card.Title>
      </Card.Header>

      <Card.Content className="flex flex-col gap-4">
        {/*
          `fullWidth` so the four options share the card's width rather than
          huddling at the left edge — measured at 320/360/390, where the Russian
          labels are what set the minimum.
        */}
        <ToggleButtonGroup
          fullWidth
          size="sm"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([period])}
          onSelectionChange={(keys) => {
            const next = [...keys][0];
            if (typeof next === 'string') setPeriod(next as ActivityPeriod);
          }}
          aria-label="Период"
        >
          {ACTIVITY_PERIODS.map((id) => (
            <ToggleButton key={id} id={id}>
              {PERIOD_LABELS[id]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {loadError && <Alert status="danger">{loadError}</Alert>}
        {/* Only the FIRST load. Every switch after it keeps the previous rows
            on screen, so there is nothing here for the loader to stand in for. */}
        {!loadError && data === null && <InlineLoader />}

        {data && data.entries.length === 0 && (
          <p className="text-sm text-muted">За этот период в чате никто не писал.</p>
        )}

        {/*
          ⚠️ SHOWN ABOVE THE BARS, NOT INSTEAD OF THEM. Three messages in a week
          make one person «67 % чата», which is arithmetic rather than
          information — but hiding the card would leave a reader wondering
          whether it was broken. The numbers stay, with the caveat attached to
          them.
        */}
        {data && !data.enough && data.entries.length > 0 && (
          <p className="text-sm text-warning">
            Сообщений за период слишком мало, чтобы проценты что-то значили.
          </p>
        )}

        {shown.length > 0 && (
          /*
            ⚠️ DIMMED WHILE REFRESHING, NEVER EMPTIED. For the length of one
            request these rows belong to the PREVIOUS period while the switcher
            already shows the new one, and the dimming is what says so. It costs
            no layout — opacity moves nothing — which is the entire point.

            `aria-busy` carries the same fact to a reader who cannot see the
            dimming, and `transition-opacity` keeps a fast reply from flickering.
          */
          <ul
            aria-busy={refreshing}
            className={cn(
              'flex flex-col gap-2.5 transition-opacity',
              refreshing && 'opacity-50',
            )}
          >
            {shown.map((row) => {
              const isOwn = ownMemberId !== null && row.telegramUserId === ownMemberId;
              return (
                <li key={row.telegramUserId} className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-2">
                    {/*
                      ⚠️ THE HANDLE IS ON THE SAME LINE, NOT A SECOND ONE. The
                      roster and the loot queue put «Евгений · @melkor» under the
                      character name, but those are table cells with room to
                      spare; here every row already spends a second line on its
                      bar, and a third would add ~180px to a ten-row card sitting
                      at the bottom of a phone screen.

                      ⚠️ IT SHRINKS BEFORE THE NAME DOES. Both truncate, and at
                      320px something has to: `min-w-0` on the name with
                      `shrink` on the handle means a long pair loses the end of
                      the @handle rather than the start of the person.
                    */}
                    <span className="flex min-w-0 items-baseline gap-1.5">
                      <span className="truncate text-sm">{row.label}</span>
                      {row.handle && (
                        <span className="min-w-0 shrink truncate text-xs text-muted">
                          {row.handle}
                        </span>
                      )}
                      {isOwn && (
                        <Chip size="sm" variant="soft" color="accent" className="shrink-0">
                          вы
                        </Chip>
                      )}
                    </span>
                    <span className="shrink-0 text-sm tabular text-muted">
                      {formatShare(row.share)}
                    </span>
                  </div>
                  {/*
                    ⚠️ `aria-hidden`, BECAUSE THE PERCENTAGE ABOVE IT IS THE
                    ACCESSIBLE VERSION. A `role="progressbar"` here would make a
                    reader hear every share twice — once as a number and once as
                    a bar — for a list that is already a name-and-value pair.
                  */}
                  <span
                    aria-hidden="true"
                    className="flex h-1.5 overflow-hidden rounded-full bg-surface-secondary"
                  >
                    <span
                      className={isOwn ? 'h-full rounded-full bg-accent' : 'h-full rounded-full bg-accent/50'}
                      style={{ width: `${Math.max(row.share * 100, 1.5)}%` }}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {/*
          ⚠️ NO COUNT ON THE LABEL. «Показать всех (9)» named the total, which is
          the one number this card is built not to show: it is the size of the
          list, not of anybody's activity, but it sits under a column of
          percentages where it reads as one more figure to compare. «Показать
          ещё» says what the press does and nothing else.
        */}
        {named.length > VISIBLE_ROWS && (
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            onPress={() => setExpanded((open) => !open)}
          >
            {expanded ? 'Свернуть' : 'Показать ещё'}
          </Button>
        )}

        {/* No full stop after the date: `ru-RU` renders «29 июня 2026 г.», which
            already ends in one, and adding ours produced «г..». */}
        <p className="text-xs text-muted">
          Доля сообщений в группе за период. Сообщения бота не считаются.
          {data?.since && ` Записи есть с ${formatSince(data.since)}`}
        </p>
      </Card.Content>
    </Card>
  );
}

/**
 * The first day the log actually covers.
 *
 * ⚠️ RENDERED FOR EVERY PERIOD, NOT JUST «ВСЁ ВРЕМЯ». It is the honest bound on
 * all four: the message log starts when the bot was switched on, so a «Год»
 * that only has three weeks in it would otherwise present three weeks as a
 * year. Pinned to `ru-RU` like every other date in this app — a bare
 * `toLocaleDateString()` renders in the VIEWER's locale, and «5/12/2026» inside
 * a Russian sentence gives no way to tell which number is the month.
 */
function formatSince(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
